import {
  Risk,
  RISK_CATEGORY,
  RISK_MATH,
  RISK_STATUS,
  RISK_TREATMENT,
} from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { range } from 'lodash'
import palette from 'src/designSystem/variables/palette'

export interface RiskCategory {
  name: string
  value: number
  sortOrder?: number
}

export type RiskValue =
  | string
  | string[]
  | RISK_STATUS
  | RISK_MATH
  | RISK_CATEGORY
  | RISK_TREATMENT

export enum RiskFields {
  id = 'id',
  accountId = 'accountId',
  name = 'name',
  description = 'description',
  departmentId = 'departmentId',
  departmentIds = 'departmentIds',
  customId = 'customId',
  status = 'status',
  customStatusId = 'customStatusId',
  ownerEmail = 'ownerEmail',
  predisposingCondition = 'preDisposingCondition',
  assetAtRisk = 'assetAtRisk',
  impactToCustomer = 'impactToCustomer',
  likelihood = 'likelihood',
  adverseImpact = 'adverseImpact',
  treatment = 'treatment',
  riskAfterTreatment = 'riskAfterTreatment',
  category = 'category',
  targetLikelihood = 'targetLikelihood',
  targetAdverseImpact = 'targetAdverseImpact',
  lastReviewedAt = 'lastReviewedAt',
  // Calculated Risk
  inherentRisk = 'inherentRisk',
  actualResidualRisk = 'actualResidualRisk',
}

export const RISK_GRID_TEMPLATE_COLUMNS =
  'min-content min-content minmax(auto, 30%) min-content repeat(9, auto)'

export const RISK_HEADERS = [
  {
    key: 'department',
    name: 'Dept',
    isCentered: false,
  },
  {
    key: 'id',
    name: 'ID',
    isCentered: false,
  },
  {
    key: 'threat',
    name: 'Threat', // This is the name property on a Risk
    isCentered: false,
  },
  {
    key: 'category',
    name: 'Category',
    isCentered: false,
  },
  {
    key: 'controls',
    name: 'Controls',
    isCentered: false,
  },
  {
    key: 'inherentRisk',
    name: 'Inherent risk',
    isCentered: false,
  },
  {
    key: 'targetResidualRisk',
    name: 'Target residual risk',
    isCentered: false,
  },
  {
    key: 'currentResidualRisk',
    name: 'Current residual risk',
    isCentered: false,
  },
  {
    key: 'lastReviewed',
    name: 'Last reviewed',
    isCentered: false,
  },
  {
    key: 'status',
    name: 'Status',
    isCentered: false,
  },
  {
    key: 'owner',
    name: 'Owner',
    isCentered: false,
  },
  {
    key: 'assignees',
    name: 'Assignees',
    isCentered: false,
  },
  {
    key: 'menu',
    name: '', // Empty column for 3-dot menu
    isCentered: false,
  },
]

export const RISK_CONTROL_HEADERS = [
  {
    name: 'Dept',
    isCentered: false,
    sortable: true,
  },
  {
    name: 'ID',
    isCentered: false,
    sortable: true,
  },
  {
    name: 'Threat',
    isCentered: false,
    sortable: true,
  },
  {
    name: 'Control effectiveness',
    isCentered: true,
    sortable: true,
  },
  {
    name: '',
    isCentered: true,
    sortable: false,
  },
]

export const RISKS_STATUS_DROPDOWN = [
  RISK_STATUS.TODO,
  RISK_STATUS.IN_PROGRESS,
  RISK_STATUS.PENDING,
  RISK_STATUS.COMPLETED,
]

export enum RiskStatusLabels {
  TODO = 'To Do',
  IN_PROGRESS = 'In Progress',
  PENDING = 'Pending',
  COMPLETED = 'Completed',
}

export const RISK_STATUS_LABEL: Record<RISK_STATUS, string> = {
  [RISK_STATUS.TODO]: RiskStatusLabels.TODO,
  [RISK_STATUS.IN_PROGRESS]: RiskStatusLabels.IN_PROGRESS,
  [RISK_STATUS.PENDING]: RiskStatusLabels.PENDING,
  [RISK_STATUS.COMPLETED]: RiskStatusLabels.COMPLETED,
}

export const RISK_STATUS_BG: Record<RISK_STATUS, string> = {
  [RISK_STATUS.TODO]: 'white',
  [RISK_STATUS.IN_PROGRESS]: 'white',
  [RISK_STATUS.PENDING]: 'white',
  [RISK_STATUS.COMPLETED]: palette.green['100'],
}

export const RISK_STATUS_BORDER: Record<RISK_STATUS, string> = {
  [RISK_STATUS.TODO]: palette.neutral['200'],
  [RISK_STATUS.IN_PROGRESS]: palette.neutral['200'],
  [RISK_STATUS.PENDING]: palette.neutral['200'],
  [RISK_STATUS.COMPLETED]: palette.green['700'],
}

export const RISK_LABEL_TO_STATUS: Record<string, RISK_STATUS> = {
  [RiskStatusLabels.TODO]: RISK_STATUS.TODO,
  [RiskStatusLabels.IN_PROGRESS]: RISK_STATUS.IN_PROGRESS,
  [RiskStatusLabels.PENDING]: RISK_STATUS.PENDING,
  [RiskStatusLabels.COMPLETED]: RISK_STATUS.COMPLETED,
}

export const RISK_STATUS_FILTER = [
  {
    value: RISK_STATUS.TODO,
    name: RiskStatusLabels.TODO,
    color: RISK_STATUS_BG[RISK_STATUS.TODO],
    border: RISK_STATUS_BORDER[RISK_STATUS.TODO],
  },
  {
    value: RISK_STATUS.IN_PROGRESS,
    name: RiskStatusLabels.IN_PROGRESS,
    color: RISK_STATUS_BG[RISK_STATUS.IN_PROGRESS],
    border: RISK_STATUS_BORDER[RISK_STATUS.IN_PROGRESS],
  },
  {
    value: RISK_STATUS.PENDING,
    name: RiskStatusLabels.PENDING,
    color: RISK_STATUS_BG[RISK_STATUS.PENDING],
    border: RISK_STATUS_BORDER[RISK_STATUS.PENDING],
  },
  {
    value: RISK_STATUS.COMPLETED,
    name: RiskStatusLabels.COMPLETED,
    color: RISK_STATUS_BG[RISK_STATUS.COMPLETED],
    border: RISK_STATUS_BORDER[RISK_STATUS.COMPLETED],
  },
]

export enum RiskMathLabels {
  NOT_SET = 'Not set',
  VERY_LOW = 'Very low',
  LOW = 'Low',
  MODERATE = 'Moderate',
  HIGH = 'High',
  VERY_HIGH = 'Very high',
}

export const RISK_MATH_LABEL: Record<RISK_MATH, RiskMathLabels> = {
  [RISK_MATH.NOT_SET]: RiskMathLabels.NOT_SET,
  [RISK_MATH.VERY_LOW]: RiskMathLabels.VERY_LOW,
  [RISK_MATH.LOW]: RiskMathLabels.LOW,
  [RISK_MATH.MODERATE]: RiskMathLabels.MODERATE,
  [RISK_MATH.HIGH]: RiskMathLabels.HIGH,
  [RISK_MATH.VERY_HIGH]: RiskMathLabels.VERY_HIGH,
}

export const RISK_MATH_BG: Record<RISK_MATH, string> = {
  [RISK_MATH.NOT_SET]: palette.blue['100'],
  [RISK_MATH.VERY_LOW]: palette.orange['100'],
  [RISK_MATH.LOW]: palette.orange['300'],
  [RISK_MATH.MODERATE]: palette.orange['700'],
  [RISK_MATH.HIGH]: palette.magenta['200'],
  [RISK_MATH.VERY_HIGH]: palette.magenta['500'],
}

// Font color override for ratings whose background is too dark for the default
// (near-black) text. Only "Very high" (dark magenta) needs white text.
export const RISK_MATH_FONT: Record<RISK_MATH, string | undefined> = {
  [RISK_MATH.NOT_SET]: undefined,
  [RISK_MATH.VERY_LOW]: undefined,
  [RISK_MATH.LOW]: undefined,
  [RISK_MATH.MODERATE]: undefined,
  [RISK_MATH.HIGH]: undefined,
  [RISK_MATH.VERY_HIGH]: palette.neutral.white,
}

export const RISK_MATH_VALUES: Record<RISK_MATH, number> = {
  [RISK_MATH.NOT_SET]: 0,
  [RISK_MATH.VERY_LOW]: 10,
  [RISK_MATH.LOW]: 30,
  [RISK_MATH.MODERATE]: 50,
  [RISK_MATH.HIGH]: 70,
  [RISK_MATH.VERY_HIGH]: 90,
}

export const RISK_MATH_LABEL_TO_RISK_MATH: Record<string, RISK_MATH> = {
  [RiskMathLabels.NOT_SET.toLowerCase()]: RISK_MATH.NOT_SET,
  [RiskMathLabels.VERY_LOW.toLowerCase()]: RISK_MATH.VERY_LOW,
  [RiskMathLabels.LOW.toLowerCase()]: RISK_MATH.LOW,
  [RiskMathLabels.MODERATE.toLowerCase()]: RISK_MATH.MODERATE,
  [RiskMathLabels.HIGH.toLowerCase()]: RISK_MATH.HIGH,
  [RiskMathLabels.VERY_HIGH.toLowerCase()]: RISK_MATH.VERY_HIGH,
}

export const RISK_MATH_LEGEND = [
  {
    label: RiskMathLabels.VERY_LOW,
    range: range(1, 20),
    color: RISK_MATH_BG[RISK_MATH.VERY_LOW],
  },
  {
    label: RiskMathLabels.LOW,
    range: range(21, 40),
    color: RISK_MATH_BG[RISK_MATH.LOW],
  },
  {
    label: RiskMathLabels.MODERATE,
    range: range(41, 60),
    color: RISK_MATH_BG[RISK_MATH.MODERATE],
  },
  {
    label: RiskMathLabels.HIGH,
    range: range(61, 80),
    color: RISK_MATH_BG[RISK_MATH.HIGH],
  },
  {
    label: RiskMathLabels.VERY_HIGH,
    range: range(81, 100),
    color: RISK_MATH_BG[RISK_MATH.VERY_HIGH],
  },
]

// RISK_MATH.NOT_SET will be the default value for all dropdowns but cannot be selected once unset
export const RISK_MATH_DROPDOWN = [
  {
    name: RiskMathLabels.VERY_LOW,
    value: RISK_MATH.VERY_LOW,
    color: RISK_MATH_BG[RISK_MATH.VERY_LOW],
  },
  {
    name: RiskMathLabels.LOW,
    value: RISK_MATH.LOW,
    color: RISK_MATH_BG[RISK_MATH.LOW],
  },
  {
    name: RiskMathLabels.MODERATE,
    value: RISK_MATH.MODERATE,
    color: RISK_MATH_BG[RISK_MATH.MODERATE],
  },
  {
    name: RiskMathLabels.HIGH,
    value: RISK_MATH.HIGH,
    color: RISK_MATH_BG[RISK_MATH.HIGH],
  },
  {
    name: RiskMathLabels.VERY_HIGH,
    value: RISK_MATH.VERY_HIGH,
    color: RISK_MATH_BG[RISK_MATH.VERY_HIGH],
  },
]

/**
 * This is the hard-coded math calculations for our Impact and Likelihood risk matrix
 *
 * The first level is the impact which then gives the lookup for the likelihood
 *
 * The matrix is asymmetric, so the key order matters: `[MODERATE][VERY_HIGH]` is
 * HIGH while `[VERY_HIGH][MODERATE]` is MODERATE. Copies live in
 * ntrced/table/risk.go and three ml-features modules; keep them in the same
 * orientation.
 */
export const RISK_MATH_MAP: Record<RISK_MATH, Record<RISK_MATH, RISK_MATH>> = {
  [RISK_MATH.NOT_SET]: {
    [RISK_MATH.NOT_SET]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_LOW]: RISK_MATH.NOT_SET,
    [RISK_MATH.LOW]: RISK_MATH.NOT_SET,
    [RISK_MATH.MODERATE]: RISK_MATH.NOT_SET,
    [RISK_MATH.HIGH]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_HIGH]: RISK_MATH.NOT_SET,
  },
  [RISK_MATH.VERY_LOW]: {
    [RISK_MATH.NOT_SET]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_LOW]: RISK_MATH.VERY_LOW,
    [RISK_MATH.LOW]: RISK_MATH.VERY_LOW,
    [RISK_MATH.MODERATE]: RISK_MATH.VERY_LOW,
    [RISK_MATH.HIGH]: RISK_MATH.LOW,
    [RISK_MATH.VERY_HIGH]: RISK_MATH.LOW,
  },
  [RISK_MATH.LOW]: {
    [RISK_MATH.NOT_SET]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_LOW]: RISK_MATH.VERY_LOW,
    [RISK_MATH.LOW]: RISK_MATH.LOW,
    [RISK_MATH.MODERATE]: RISK_MATH.LOW,
    [RISK_MATH.HIGH]: RISK_MATH.LOW,
    [RISK_MATH.VERY_HIGH]: RISK_MATH.MODERATE,
  },
  [RISK_MATH.MODERATE]: {
    [RISK_MATH.NOT_SET]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_LOW]: RISK_MATH.VERY_LOW,
    [RISK_MATH.LOW]: RISK_MATH.LOW,
    [RISK_MATH.MODERATE]: RISK_MATH.MODERATE,
    [RISK_MATH.HIGH]: RISK_MATH.MODERATE,
    [RISK_MATH.VERY_HIGH]: RISK_MATH.HIGH,
  },
  [RISK_MATH.HIGH]: {
    [RISK_MATH.NOT_SET]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_LOW]: RISK_MATH.VERY_LOW,
    [RISK_MATH.LOW]: RISK_MATH.LOW,
    [RISK_MATH.MODERATE]: RISK_MATH.MODERATE,
    [RISK_MATH.HIGH]: RISK_MATH.HIGH,
    [RISK_MATH.VERY_HIGH]: RISK_MATH.VERY_HIGH,
  },
  [RISK_MATH.VERY_HIGH]: {
    [RISK_MATH.NOT_SET]: RISK_MATH.NOT_SET,
    [RISK_MATH.VERY_LOW]: RISK_MATH.VERY_LOW,
    [RISK_MATH.LOW]: RISK_MATH.LOW,
    [RISK_MATH.MODERATE]: RISK_MATH.MODERATE,
    [RISK_MATH.HIGH]: RISK_MATH.HIGH,
    [RISK_MATH.VERY_HIGH]: RISK_MATH.VERY_HIGH,
  },
}

export enum RiskCategoryLabels {
  NO_CATEGORY = 'No Category',
  CUSTOMER = 'Customer Commitments',
  VENDOR = 'Vendor Management',
  LEADERSHIP = 'Leadership Commitment',
  SYSTEMS = 'Systems and Technology Changes',
  LAWS = 'Laws, Regulations, and Standards',
  IP = 'Intellectual Property',
}

export const RISK_CATEGORY_LABEL: Record<number, RiskCategoryLabels> = {
  [RISK_CATEGORY.NO_CATEGORY]: RiskCategoryLabels.NO_CATEGORY,
  [RISK_CATEGORY.CUSTOMER]: RiskCategoryLabels.CUSTOMER,
  [RISK_CATEGORY.VENDOR]: RiskCategoryLabels.VENDOR,
  [RISK_CATEGORY.LEADERSHIP]: RiskCategoryLabels.LEADERSHIP,
  [RISK_CATEGORY.SYSTEMS]: RiskCategoryLabels.SYSTEMS,
  [RISK_CATEGORY.LAWS]: RiskCategoryLabels.LAWS,
  [RISK_CATEGORY.IP]: RiskCategoryLabels.IP,
}

// RISK_CATEGORY.NO_CATEGORY will be set by default for all dropdowns but cannot be selected once unset
export const RISK_CATEGORY_DROPDOWN = [
  {
    name: RiskCategoryLabels.CUSTOMER,
    value: RISK_CATEGORY.CUSTOMER as number,
  },
  {
    name: RiskCategoryLabels.VENDOR,
    value: RISK_CATEGORY.VENDOR as number,
  },
  {
    name: RiskCategoryLabels.LEADERSHIP,
    value: RISK_CATEGORY.LEADERSHIP as number,
  },
  {
    name: RiskCategoryLabels.SYSTEMS,
    value: RISK_CATEGORY.SYSTEMS as number,
  },
  {
    name: RiskCategoryLabels.LAWS,
    value: RISK_CATEGORY.LAWS as number,
  },
  {
    name: RiskCategoryLabels.IP,
    value: RISK_CATEGORY.IP as number,
  },
]

export enum RiskTreatmentLabels {
  ACCEPT = 'Accept',
  AVOID = 'Avoid',
  MITIGATE = 'Mitigate',
  SHARE = 'Share',
  TRANSFER = 'Transfer',
}

export const RISK_TREATMENT_LABEL: Record<RISK_TREATMENT, string> = {
  [RISK_TREATMENT.ACCEPT]: RiskTreatmentLabels.ACCEPT,
  [RISK_TREATMENT.AVOID]: RiskTreatmentLabels.AVOID,
  [RISK_TREATMENT.MITIGATE]: RiskTreatmentLabels.MITIGATE,
  [RISK_TREATMENT.SHARE]: RiskTreatmentLabels.SHARE,
  [RISK_TREATMENT.TRANSFER]: RiskTreatmentLabels.TRANSFER,
}

export const RISK_TREATMENT_DROPDOWN = [
  {
    name: RiskTreatmentLabels.ACCEPT,
    value: RISK_TREATMENT.ACCEPT,
  },
  {
    name: RiskTreatmentLabels.AVOID,
    value: RISK_TREATMENT.AVOID,
  },
  {
    name: RiskTreatmentLabels.MITIGATE,
    value: RISK_TREATMENT.MITIGATE,
  },
  {
    name: RiskTreatmentLabels.SHARE,
    value: RISK_TREATMENT.SHARE,
  },
  {
    name: RiskTreatmentLabels.TRANSFER,
    value: RISK_TREATMENT.TRANSFER,
  },
]

export const RISK_VALIDATORS = {
  ID: 10,
  NAME: 255,
}

export interface AddControlsToRiskProps {
  existingControlIds: string[]
  riskId: string
}

export interface AddRisksToControlProps {
  existingRiskIds: string[]
  controlId: string
}

export interface RiskControl {
  id: string
  dept: string
  threat: string
  control_effectiveness: boolean
  risk: Risk
  control: Control
}

/** The bits of a linked control the risk grid needs to label it. */
export interface RiskControlSummary {
  id: string
  modelId: string
  name: string
}

/** Linked-control chips shown inline on a risk row before the "+ N" overflow chip. */
export const MAX_VISIBLE_RISK_CONTROLS = 2

/** Control IDs are short; cap the chip so a name fallback can't widen the column. */
export const RISK_CONTROL_CHIP_MAX_WIDTH = '110px'
